import React, { useState, useEffect, useRef } from 'react';
import { taskDetailsService } from '../features/task/services/taskDetailsService';
import { getCurrentStatus, STATUS_NAME_REPORTED, STATUS_NAME_OPEN, STATUS_WORK_COMPLETED,STATUS_NAME_WORK_REPORT_COMPLETED,STATUS_NAME_WORK_ON_HOLD,STATUS_NAME_INSPECTION_SKIPPED,STATUS_NAME_ESTIMATION_SKIPPED,STATUS_NAME_ESTIMATION_APPROVED,STATUS_REJECTED} from '../shared/constants/statusConstants';
import { parseAndConvertToLocal, twentyFourHrFormat,convertMinutesToHoursAndMinutesShort } from '../shared/utils/dateFormatter';
import './StepperComponent.css';
import { FiChevronDown, FiChevronUp } from "react-icons/fi";

const StepperComponent = ({ taskId, isMobile, reportedDate, openDate, maintenanceData, onEstimationConfirm, onWorkReportConfirm, onFeedbackSubmit, signaturesLoading }) => {
  const [expandedStep, setExpandedStep] = useState(null);
  const [rating, setRating] = useState(0);
  const [comments, setComments] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const drawingRef = useRef(false);

  const toggleStep = (index) => setExpandedStep(expandedStep === index ? null : index);

  const getServiceCategoryLabel = (value) => {
    const list = [{ label: 'PAID', value: 0 }, { label: 'UNDER WARRANTY', value: 1 }, { label: 'AMC', value: 3 }];
    return list.find(i => i.value === value)?.label || 'N/A';
  };

  const formatCurrency = (amount) => amount ? `₹${amount}` : 'N/A';

  const getPriorityText = (priority) => {
    const map = { 0: 'Low', 1: 'Medium', 2: 'Normal', 3: 'High', 4: 'Urgent' };
    return map[priority] || 'N/A';
  };

  const setupSignatureCanvas = (canvasId, isReadOnly) => {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    canvas.style.cursor = isReadOnly ? 'default' : 'crosshair';
    canvas.style.pointerEvents = isReadOnly ? 'none' : 'auto';
    canvas.style.touchAction = 'none';
  };

  const clearSignature = (canvasId) => {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.beginPath();
    drawingRef.current = false;
  };

  useEffect(() => {
    if (expandedStep === null) return;
    const timer = setTimeout(() => {
      const estReadOnly = (maintenanceData?.estimationSignature?.length > 0);
      const wrReadOnly = (maintenanceData?.serviceReportSignature?.length > 0);
      setupSignatureCanvas('stepper-est-canvas', estReadOnly);
      setupSignatureCanvas('stepper-wr-canvas', wrReadOnly);

      const loadImg = (canvasId, url) => {
        if (!url) return;
        const canvas = document.getElementById(canvasId);
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => { ctx.clearRect(0,0,canvas.width,canvas.height); ctx.drawImage(img,5,5,canvas.width-10,canvas.height-10); };
        img.src = url;
      };

      if (maintenanceData?.estimationSignature?.[0]) loadImg('stepper-est-canvas', maintenanceData.estimationSignature[0]);
      if (maintenanceData?.serviceReportSignature?.[0]) loadImg('stepper-wr-canvas', maintenanceData.serviceReportSignature[0]);

      const attachDrawing = (canvasId) => {
        const canvas = document.getElementById(canvasId);
        if (!canvas || canvas.style.pointerEvents === 'none') return;
        const getPos = (e) => {
          const rect = canvas.getBoundingClientRect();
          const src = e.touches ? e.touches[0] : e;
          return { x: src.clientX - rect.left, y: src.clientY - rect.top };
        };
        const onStart = (e) => { e.preventDefault(); drawingRef.current = true; const ctx = canvas.getContext('2d'); const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
        const onMove = (e) => { if (!drawingRef.current) return; e.preventDefault(); const ctx = canvas.getContext('2d'); const p = getPos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
        const onEnd = (e) => { e.preventDefault(); drawingRef.current = false; };
        canvas.addEventListener('mousedown', onStart, { passive: false });
        canvas.addEventListener('mousemove', onMove, { passive: false });
        canvas.addEventListener('mouseup', onEnd, { passive: false });
        canvas.addEventListener('mouseleave', onEnd, { passive: false });
        canvas.addEventListener('touchstart', onStart, { passive: false });
        canvas.addEventListener('touchmove', onMove, { passive: false });
        canvas.addEventListener('touchend', onEnd, { passive: false });
      };
      attachDrawing('stepper-est-canvas');
      attachDrawing('stepper-wr-canvas');
    }, 250);
    return () => clearTimeout(timer);
  }, [expandedStep, maintenanceData]);

  useEffect(() => {
    if (!taskId) return;
    taskDetailsService.getFeedbackDetails(taskId).then(data => {
      const existing = data?.data?.[0];
      if (existing) {
        setRating(existing.rating || 0);
        setComments(existing.comment || '');
        setSelectedTags(existing.tags || []);
        setFeedbackSubmitted(true);
      }
    }).catch(() => {});
  }, [taskId]);

  const handleEstConfirm = async (canvasId, nameId) => {
    const name = document.getElementById(nameId)?.value?.trim();
    if (!name) { alert('Name is required'); return; }
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const hasSignature = ctx.getImageData(0,0,canvas.width,canvas.height).data.some(c => c !== 0);
    if (!hasSignature) { alert('Please sign to confirm'); return; }
    canvas.toBlob(blob => onEstimationConfirm && onEstimationConfirm(blob, name), 'image/png');
  };

  const handleWrConfirm = async (canvasId, nameId) => {
    const name = document.getElementById(nameId)?.value?.trim();
    if (!name) { alert('Name is required'); return; }
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const hasSignature = ctx.getImageData(0,0,canvas.width,canvas.height).data.some(c => c !== 0);
    if (!hasSignature) { alert('Please sign to confirm'); return; }
    canvas.toBlob(async (blob) => {
      if (!blob) { alert('Failed to capture signature'); return; }
      try {
        await onWorkReportConfirm(blob, name);
      } catch (e) {
        console.error('Work report confirm error:', e);
        alert('Error confirming work report');
      }
    }, 'image/png');
  };

  const handleFeedbackSubmit = async () => {
    setIsSubmittingFeedback(true);
    try {
      const ok = await onFeedbackSubmit?.({ rating, comment: comments, tags: selectedTags });
      if (ok) setFeedbackSubmitted(true);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const renderStepPanel = (step) => {
    const md = maintenanceData;
    const label = step.statusText;

    if (label === 'Reported') return (
      <div className="step-panel">
        <div className="step-panel-tags">
          {md?.issues?.details?.map((issue, i) => <span key={i} className="issue-tag">{issue}</span>) || <span className="issue-tag">No issues</span>}
        </div>
        <p><b>Reported:</b> {md?.reportedDate ? twentyFourHrFormat(parseAndConvertToLocal(md.reportedDate), true) : 'N/A'}</p>
        <p><b>Priority:</b> {getPriorityText(md?.schedule?.priority)}</p>
        <p><b>Request ID:</b> {md?.reqId || 'N/A'}</p>
      </div>
    );

    if (label === 'Open') return (
      <div className="step-panel">
        <p><b>Company:</b> {md?.companyName || 'N/A'}</p>
        <p><b>Phone:</b> {md?.phoneNumber || 'N/A'}</p>
        <p><b>Email:</b> {md?.email || 'N/A'}</p>
        <p><b>Address:</b> {md?.address?.fullAddress || 'N/A'}</p>
      </div>
    );

    if (label === 'Accepted') return (
      <div className="step-panel">
        <p><b>Equipment:</b> {md?.equipment?.productName || 'N/A'}</p>
        <p><b>Make:</b> {md?.equipment?.make || 'N/A'}</p>
        <p><b>Model:</b> {md?.equipment?.model || 'N/A'}</p>
        <p><b>Warranty:</b> {getServiceCategoryLabel(md?.warrantyType)}</p>
        <p><b>Scheduled:</b> {md?.schedule?.scheduledDate && md.schedule.scheduledDate !== 'N/A' ? twentyFourHrFormat(md.schedule.scheduledDate, true) : 'N/A'}</p>
        {/* <p><b>Duration:</b> {md?.schedule?.taskDuration || 'N/A'}</p> */}
        <p><b>Technician:</b> {md?.technician?.name || 'N/A'}</p>
      </div>
    );

    if (label === 'Inspection completed' || label === 'Inspection skipped') return (
      <div className="step-panel">
        <p><b>Findings &amp; Recommendations</b></p>
        {md?.inspectionComments?.length > 0
          ? md.inspectionComments.map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '8px', padding: '8px 10px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e8e8e8' }}>
              <div style={{ minWidth: '22px', height: '22px', borderRadius: '50%', background: '#35605a', color: '#fff', fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontWeight: 600 }}>{i + 1}</div>
              <div style={{ flex: 1 }}>
                {item.comments?.trim() && (
                  <p style={{ margin: '0 0 6px', fontSize: '13px', color: '#333', lineHeight: '1.6' }}>{item.comments}</p>
                )}
                {item.file && (
                  <img src={item.file} alt={`finding-${i}`} style={{ width: '100%', maxWidth: '200px', height: '130px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #ddd' }} onError={e => e.target.style.display = 'none'} />
                )}
              </div>
            </div>
          ))
          : <p style={{ color: '#888', fontSize: '13px' }}>No findings available</p>
        }
        {md?.inspectionFiles?.filter(url => !md?.inspectionComments?.some(c => c.file === url)).length > 0 && (
          <>
            <p style={{ fontWeight: 600, fontSize: '13px', color: '#374151', margin: '12px 0 8px' }}>Inspection Photos</p>
            <div className="step-panel-photo-grid">
              {md.inspectionFiles.filter(url => !md?.inspectionComments?.some(c => c.file === url)).map((url, i) => (
                <img key={i} src={url} alt={`insp-${i}`} className="step-panel-photo" onError={e => e.target.style.display = 'none'} />
              ))}
            </div>
          </>
        )}
      </div>
    );

    if (label === 'Estimation completed') return (
      <div className="step-panel">
        {md?.estimation ? (
          <>
            <p><b>Labour Cost:</b> ₹{md.estimation.labour_cost?.totalLabourCost || 'N/A'}</p>
            <p><b>Duration:</b> {convertMinutesToHoursAndMinutesShort(md.estimation.labour_cost?.durationInMinutes) || 'N/A'}</p>
            <p><b>Rate:</b> ₹{md.estimation.labour_cost?.rate || 'N/A'}</p>
            <p style={{marginTop:'8px'}}><b>Spare Parts:</b></p>
            {md.estimation.spare_parts_cost?.map((part, i) => (
              <p key={i} style={{paddingLeft:'10px'}}>• {part?.name} (Qty: {part?.qty}) — ₹{part?.totalSpareCost}</p>
            ))}
            <hr style={{border:'0.1px solid #555',margin:'10px 0'}} />
            <p><b>Grand Total: ₹{md.estimation.totalAmount || 'N/A'}</b></p>
            {md.estimation.confirmed_by && <p><b>Confirmed By:</b> {md.estimation.confirmed_by}</p>}
          </>
        ) : <p>No estimation data</p>}
        <div style={{marginTop:'16px'}}>
          <p>{md?.estimationSignature?.length > 0 ? 'Estimation Confirmed ✓' : 'Sign to confirm estimation'}</p>
          {!(md?.estimationSignature?.length > 0) && (
            <input type="text" id="stepper-est-name" placeholder="Name*" className='sign-name-input' />
          )}
          <canvas id="stepper-est-canvas" className='sign-canvas' />
          {!(md?.estimationSignature?.length > 0) && (
            <div style={{
    display: "flex",
    gap: "8px",
    marginTop: "8px",
    justifyContent: "flex-end"
  }}>
              <button style={{
      padding: "6px 14px",
      borderRadius: "4px",
      border: "1px solid #ccc",
      background: "#f5f5f5",
      cursor: "pointer"
    }} onClick={() => clearSignature('stepper-est-canvas')}>Clear</button>
              <button style={{
      padding: "6px 14px",
      borderRadius: "4px",
      border: "none",
      background: "#007bff",
      color: "#fff",
      cursor: "pointer"
    }} onClick={() => handleEstConfirm('stepper-est-canvas','stepper-est-name')}>Confirm</button>
            </div>
          )}
        </div>
      </div>
    );

    if (label === 'Work report completed') return (
      <div className="step-panel">
        <p><b>Work Done</b></p>
        {md?.workDoneComments?.length > 0
          ? md.workDoneComments.map((c, i) => (
            <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '8px', padding: '8px 10px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e8e8e8' }}>
              <div style={{ minWidth: '22px', height: '22px', borderRadius: '50%', background: '#35605a', color: '#fff', fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontWeight: 600 }}>{i + 1}</div>
              <div style={{ flex: 1 }}>
                {(typeof c === 'object' ? c.comments : c) && (
                  <p style={{ margin: '0 0 6px', fontSize: '13px', color: '#333', lineHeight: '1.6' }}>{typeof c === 'object' ? c.comments : c}</p>
                )}
                {typeof c === 'object' && c.file && (
                  <img src={c.file} alt={`work-${i}`} style={{ width: '100%', maxWidth: '200px', height: '130px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #ddd' }} onError={e => e.target.style.display = 'none'} />
                )}
              </div>
            </div>
          ))
          : <p>No comments</p>
        }
        <p style={{marginTop:'10px'}}><b>Work Photos</b></p>
        <div className="step-panel-photo-grid">
          {md?.workReportFiles?.length > 0
            ? md.workReportFiles.map((url, i) => <img key={i} src={url} alt={`wr-${i}`} className="step-panel-photo" onError={e => e.target.style.display='none'} />)
            : <p>No photos</p>
          }
        </div>
        <p style={{marginTop:'10px'}}><b>Spare Parts:</b></p>
        {md?.workReport?.spare_parts_cost?.map((part, i) => (
          <p key={i} style={{paddingLeft:'10px'}}>• {part?.name} (Qty: {part?.qty}) — ₹{part?.totalSpareCost}</p>
        ))}
        <p><b>Labour:</b> {formatCurrency(md?.workReport?.labour_cost?.totalLabourCost)} ({convertMinutesToHoursAndMinutesShort(md?.workReport?.labour_cost?.durationInMinutes) || 'N/A'})</p>
        <hr style={{border:'0.1px solid #555',margin:'10px 0'}} />
        <p><b>Grand Total: {formatCurrency(md?.workReport?.totalAmount)}</b></p>
        <div style={{marginTop:'16px'}}>
          <p>{md?.serviceReportSignature?.length > 0 ? 'Work Report Confirmed ✓' : 'Sign to confirm work report'}</p>
          {!(md?.serviceReportSignature?.length > 0) && (
            <input type="text" id="stepper-wr-name" placeholder="Name*" className='sign-name-input' />
          )}
          <canvas id="stepper-wr-canvas" className='sign-canvas'/>
          {!(md?.serviceReportSignature?.length > 0) && (            
            <div style={{
    display: "flex",
    gap: "8px",
    marginTop: "8px",
    justifyContent: "flex-end"
  }}>
             <button style={{
      padding: "6px 14px",
      borderRadius: "4px",
      border: "1px solid #ccc",
      background: "#f5f5f5",
      cursor: "pointer" }}
      onClick={() => clearSignature('stepper-wr-canvas')}>Clear</button>
              <button 
              style={{
      padding: "6px 14px",
      borderRadius: "4px",
      border: "none",
      background: "#007bff",
      color: "#fff",
      cursor: "pointer"
    }}
              onClick={() => handleWrConfirm('stepper-wr-canvas','stepper-wr-name')}>Confirm</button>
            </div>
          )}
        </div>
      </div>
    );

    if (label === 'Work completed') return (
      <div className="step-panel">
        {feedbackSubmitted ? (
          <>
            <p><b>Thank you for your feedback!</b></p>
            <div className="stars" style={{justifyContent:'flex-start',marginBottom:'12px'}}>
              {[1,2,3,4,5].map(s => (
                <span key={s} className={s <= rating ? 'active' : ''} style={{cursor:'default'}}>★</span>
              ))}
            </div>
            {selectedTags.length > 0 && (
              <div style={{marginBottom:'8px'}}>{selectedTags.join(', ')}</div>
            )}
            {comments && <p style={{marginBottom:'8px'}}>{comments}</p>}
          </>
        ) : (
          <>
            <p><b>Rate your experience</b></p>
            <div className="stars" style={{justifyContent:'flex-start',marginBottom:'12px'}}>
              {[1,2,3,4,5].map(s => (
                <span key={s} className={s <= rating ? 'active' : ''} onClick={() => setRating(s)} style={{cursor:'pointer'}}>★</span>
              ))}
            </div>
            <textarea placeholder="Comments" value={comments} onChange={e => setComments(e.target.value)} style={{width:'100%',minHeight:'60px',marginBottom:'8px', background:'white', border:'1px solid #aaa'}} />
            <button
              style={{padding:'6px 14px',borderRadius:'4px',border:'none',background:'#007bff',color:'#fff',cursor:'pointer'}}
              onClick={handleFeedbackSubmit} disabled={isSubmittingFeedback}>
              {isSubmittingFeedback ? 'Submitting...' : 'Submit'}
            </button>
          </>
        )}
      </div>
    );

    return null;
  };
  const [stepperList, setStepperList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const baseSteps = [
    { status: 0, statusText: 'Reported' },
    { status: 0, statusText: 'Open' },
    { status: 0, statusText: 'Accepted' },
    { status: 0, statusText: 'Inspection completed' },
    { status: 0, statusText: 'Estimation completed' },
    { status: 0, statusText: 'Estimation approved' },
    { status: 0, statusText: 'Work report completed' },
    { status: 0, statusText: 'Work completed' }
  ];

  const stepMapping = {
    'Inspection skipped': 'Inspection completed',
    'Estimation skipped': 'Estimation completed'
  };

  const getStepStatus = (status, statusText) => {
    if (status === STATUS_REJECTED || statusText === 'Rejected') {
      return 'rejected';
    }
    
    const currentStatus = getCurrentStatus(status);
    if (currentStatus === 6) return 'completed';
    if (currentStatus === 4) return 'skipped';
    if (currentStatus === 1) return 'in-progress';
    if (currentStatus === 5) return 'rejected';
    return 'pending';
  };

  const getConnectionColor = (currentStepStatus, nextStepStatus) => {
    if (currentStepStatus === 'completed' || currentStepStatus === 'in-progress') {
      return '#00bd10';
    }
    if (currentStepStatus === 'skipped') {
      return 'rgba(52, 152, 219, 0.5)';
    }
    if (currentStepStatus === 'rejected') {
      return '#FF0000';
    }
    return '#d2dcec';
  };

  const getFormattedDate = (dateString) => {
    if (!dateString) return '';
    const formatted = twentyFourHrFormat(parseAndConvertToLocal(dateString), true);
    return formatted.split(' ')[0];
  };

  const getFormattedTime = (dateString) => {
    if (!dateString) return '';
    const formatted = twentyFourHrFormat(parseAndConvertToLocal(dateString), true);
    return formatted.split(' ').slice(1).join(' ');
  };

  const getStepExtraInfo = (step) => {
    if (step.statusText === 'Assigned') {
      return `Assigned to ${step.assignedTo || 'Technician'}`;
    }
    if (step.statusText === 'Reported' && step.issueDescription) {
      return step.issueDescription;
    }
    return null;
  };

  const processStepperData = (receivedData) => {
    const uniqueMap = {};
    
    receivedData.forEach(item => {
      const key = item.status_text;
      if (!uniqueMap[key] || new Date(item.updated_at || item.created_at) > new Date(uniqueMap[key].updated_at || uniqueMap[key].created_at)) {
        uniqueMap[key] = item;
      }
    });

    const uniqueData = Object.values(uniqueMap).sort((a, b) => 
      new Date(a.created_at) - new Date(b.created_at)
    );

    // Build dynamic steps based on API response
    let steps = [...baseSteps];
    
    // Check for rejected status and replace Accepted step
    const rejectedItem = uniqueData.find(item => item.status === STATUS_REJECTED);
    if (rejectedItem) {
      const acceptedIndex = steps.findIndex(step => step.statusText === 'Accepted');
      if (acceptedIndex !== -1) {
        steps[acceptedIndex] = { 
          status: STATUS_REJECTED, 
          statusText: 'Rejected',
          createdAt: rejectedItem.created_at,
          updatedAt: rejectedItem.updated_at
        };
      }
    }
    
    // Check for skipped steps and estimation approval
    const hasSkippedInspection = uniqueData.some(item => item.status_text === STATUS_NAME_INSPECTION_SKIPPED);
    const hasSkippedEstimation = uniqueData.some(item => item.status_text === STATUS_NAME_ESTIMATION_SKIPPED);
    const hasEstimationApproval = uniqueData.some(item => item.status_text === STATUS_NAME_ESTIMATION_APPROVED);
    
    // Remove estimation approval if not present in API or if estimation is skipped
    if (!hasEstimationApproval || hasSkippedEstimation) {
      steps = steps.filter(step => step.statusText !== STATUS_NAME_ESTIMATION_APPROVED);
    }
    
    // Check for Hold status - only show if it's the latest status in original data
    const latestOriginalItem = receivedData[receivedData.length - 1];
    const showHold = latestOriginalItem && latestOriginalItem.status_text === STATUS_NAME_WORK_ON_HOLD;
    
    if (showHold) {
      const workReportIndex = steps.findIndex(step => step.statusText === STATUS_NAME_WORK_REPORT_COMPLETED);
      if (workReportIndex !== -1) {
        steps.splice(workReportIndex, 0, { status: 0, statusText: STATUS_NAME_WORK_ON_HOLD });
      }
    }
    
    // Update steps with API data
    uniqueData.forEach(apiItem => {
      const mappedStep = stepMapping[apiItem.status_text] || apiItem.status_text;
      const stepIndex = steps.findIndex(step => step.statusText === mappedStep);
      
      if (stepIndex !== -1) {
        steps[stepIndex] = {
          status: apiItem.status,
          statusText: apiItem.status_text,
          createdAt: apiItem.created_at,
          updatedAt: apiItem.updated_at,
          assignedTo: apiItem.assigned_to,
          issueDescription: apiItem.issue_description
        };
      }
    });
    
    // Set dates for Reported and Open steps
    const reportedIndex = steps.findIndex(step => step.statusText === STATUS_NAME_REPORTED);
    if (reportedIndex !== -1 && reportedDate) {
      steps[reportedIndex].createdAt = reportedDate;
      steps[reportedIndex].status = STATUS_WORK_COMPLETED;
    }
    
    const openIndex = steps.findIndex(step => step.statusText === STATUS_NAME_OPEN);
    if (openIndex !== -1 && (reportedDate || openDate)) {
      steps[openIndex].createdAt = reportedDate || openDate;
      steps[openIndex].status = STATUS_WORK_COMPLETED;
    }
    
    return steps;
  };

  useEffect(() => {
    const fetchSteps = async () => {
      if (!taskId) return;

      try {
        setIsLoading(true);
        const response = await taskDetailsService.getMaintenanceRequestDetails(taskId, 'task_history');
        
        if (response.success && response.data) {
          const processedSteps = processStepperData(response.data);
          setStepperList(processedSteps);
        }
      } catch (error) {
        setErrorMessage(error.toString());
      } finally {
        setIsLoading(false);
      }
    };

    fetchSteps();
  }, [taskId, reportedDate, openDate]);

  if (isLoading) {
    return (
      <div className="stepper-loading">
        <div className="stepper-spinner"></div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="stepper-error">
        Error: {errorMessage}
      </div>
    );
  }

  return (
  <div className={`stepper-wrapper ${isMobile ? 'mobile' : 'desktop'}`}>
    {stepperList.map((step, index) => {
      const stepNumber = (index + 1).toString().padStart(2, '0');
      const stepStatus = getStepStatus(step.status, step.statusText);
      const isFeedbackStep = step.statusText === 'Work completed';
      const effectiveStatus = isFeedbackStep && feedbackSubmitted ? 'completed' : stepStatus;
      const extraInfo = getStepExtraInfo(step);
      const nextStep = index < stepperList.length - 1 ? stepperList[index + 1] : null;
      const nextStepStatus = nextStep ? getStepStatus(nextStep.status, nextStep.statusText) : null;
      const connectionColor = nextStep ? getConnectionColor(effectiveStatus, nextStepStatus) : null;
      const isOpen = expandedStep === index;
      const panel = renderStepPanel(step);

      return (
        <div className={`mainstepper ${effectiveStatus}`} key={index}>

          <div className="step-marker-wrapper">
            <div
              className="step-marker"
              style={{ backgroundColor: isFeedbackStep && feedbackSubmitted ? '#00bd10' : connectionColor }}
            >
              {stepNumber}
            </div>

            {index < stepperList.length - 1 && (
              <div
                className="step-connector"
                style={{ backgroundColor: connectionColor }}
              />
            )}
          </div>

          <div className="step-content">
            <div
        className="step-title-row" style={panel ? { cursor: "pointer" } : {}}  onClick={panel ? () => toggleStep(index) : undefined}
      >
        <span className="step-title">{step.statusText}</span>

        {panel && (
          <span className="step-toggle-icon">
            {isOpen ? <FiChevronUp size={15}/> : <FiChevronDown size={15}/>}
          </span>
        )}
      </div>

            {step.createdAt && (
              <span className="step-meta">
                📅 {getFormattedDate(step.createdAt)} {getFormattedTime(step.createdAt)}
              </span>
            )}

            {extraInfo && (
              <span className="step-extra">{extraInfo}</span>
            )}

            {isOpen && panel}
          </div>

        </div>
      );
    })}
  </div>
);
};

export default StepperComponent;